import { useEffect, RefObject } from "react";
import { SmoothZoomHandler } from "../types/zoom";

const DEFAULT_WHEEL_ZOOM_SENSITIVITY = 0.01;
const WHEEL_ZOOM_STEP = 0.99;
const SENSITIVITY_MULTIPLIER = 100;
const MIN_SCALE_FACTOR = 0.9;
const MAX_SCALE_FACTOR = 1.1;

interface UseWheelZoomParams {
  containerRef: RefObject<HTMLElement>;
  onZoom: SmoothZoomHandler;
  wheelZoomSensitivity?: number;
}

export default function useWheelZoom({
  containerRef,
  onZoom,
  wheelZoomSensitivity = DEFAULT_WHEEL_ZOOM_SENSITIVITY,
}: UseWheelZoomParams): void {
  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const handleWheel = (event: WheelEvent): void => {
      event.preventDefault();

      const rawScaleFactor = Math.pow(
        WHEEL_ZOOM_STEP,
        event.deltaY * wheelZoomSensitivity * SENSITIVITY_MULTIPLIER,
      );
      const scaleFactor = Math.min(
        MAX_SCALE_FACTOR,
        Math.max(MIN_SCALE_FACTOR, rawScaleFactor),
      );
      onZoom(scaleFactor, event.clientX, event.clientY);
    };

    container.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      container.removeEventListener("wheel", handleWheel);
    };
  }, [containerRef, onZoom, wheelZoomSensitivity]);
}
